/* eslint-disable jsx-a11y/media-has-caption */
import classNames from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';

import { MEDIA_TYPE } from 'constants/MediaConstants';
import { downloadFetchedURL } from 'modules/medias/actions';
import { Ellipsis } from 'ui/base';
import FileTypeIcon from 'ui/base/MediaGallery/components/Medias/FileTypeIcon';
import { getExtension } from 'utils/fileName';

import { MediaCheckbox, MediaWrapper, Thumbnail } from './UI';

/** @type {React.FC<{
 * name?: string,
 * showDownload?: boolean,
 * onClick: (mediaId: string) => void,
 * className: import('classnames').Argument,
 } & import('modules/medias/types/abstract-media').AbstractMedia>} */
const Media = ({ className, onClick, selected, showCheckBox, showDownload, id: mediaId, ...media }) => {
  const dispatch = useDispatch();
  const thumbnail = media.thumbnailPath || media.path;
  const useImageViewer = Boolean(thumbnail && !thumbnail.startsWith('blob:')) && media.type !== MEDIA_TYPE.DOCUMENT;
  const [isHovering, setIsHovering] = React.useState(false);

  const extension = getExtension(media.filename);
  const displayCheckbox = showCheckBox && (isHovering || selected);
  const displayVideo = media.type === MEDIA_TYPE.VIDEO && !media.thumbnailPath;

  const nameToDisplay = React.useMemo(() => {
    const base = media.name ?? media.filename;
    if (base?.endsWith(`.${extension}`)) {
      return base.substring(0, base.length - (extension.length + 1));
    }
    return base;
  }, [extension, media]);

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const handleDownloadOnClick = React.useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      dispatch(downloadFetchedURL(media.path, media.filename));
    },
    [dispatch, media]
  );

  const handleOnClick = React.useCallback(() => {
    onClick?.(mediaId);
  }, [mediaId, onClick]);

  return (
    <MediaWrapper
      onClick={handleOnClick}
      className={classNames(className, 'd-flex flex-column justify-content-between align-items-center')}
      data-cy={`media-card-${mediaId}`}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      selected={selected}
    >
      {!displayVideo && (
        <Thumbnail className="thumbnail" background={useImageViewer ? thumbnail : undefined}>
          {!useImageViewer && <FileTypeIcon extension={extension} type="file" />}
          <MediaCheckbox checked={selected} visible={displayCheckbox} />
        </Thumbnail>
      )}
      {displayVideo && (
        <div className="d-flex justify-content-center align-items-center position-relative thumbnail">
          <video className="w-100 h-100">
            <source src={media.path} />
          </video>
          <MediaCheckbox checked={selected} visible={displayCheckbox} />
        </div>
      )}
      <div className="actions d-flex align-items-center justify-content-center mt-3">
        {media.favorite && <span className="fb-icon--star-filled mr-2" />}
        {nameToDisplay && extension && <Ellipsis tooltipProps={{ title: <div style={{ textAlign: 'center' }}>{nameToDisplay}.{extension}</div> }}>
          <span className="filename">{nameToDisplay}</span>
        </Ellipsis>}
        {extension && <span className="font-weight-bold text-lowercase">.{extension}</span>}
      </div>
      {showDownload && (
        <div className="actions d-flex align-items-center justify-content-center mt-2">
          <span className="fb-icon--download" onClick={handleDownloadOnClick} />
        </div>
      )}
    </MediaWrapper>
  );
};

export default Media;
